import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BreadcrumbSEO from '../components/BreadcrumbSEO';
import SchemaOrgRealisationSEO from '../components/SchemaOrgRealisationSEO';
import { MapPin, Calendar, X, ExternalLink, ArrowRight } from 'lucide-react';

const RealisationDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [realisation, setRealisation] = useState(null);
  const [similaires, setSimilaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchRealisation();
  }, [slug]);

  const fetchRealisation = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/realisations/${slug}`);
      
      if (response.status === 404) {
        navigate('/realisations');
        return;
      }

      const data = await response.json();
      setRealisation(data);

      // Récupérer réalisations similaires
      if (data.related_realisations && data.related_realisations.length > 0) {
        const similairesIds = data.related_realisations.slice(0, 3);
        const similairesResp = await fetch(`${BACKEND_URL}/api/realisations?limit=100`);
        const allRealisations = await similairesResp.json();
        const similairesData = allRealisations.realisations?.filter(r => similairesIds.includes(r.id)) || [];
        setSimilaires(similairesData);
      } else if (data.category) {
        const similairesResp = await fetch(`${BACKEND_URL}/api/realisations?category=${encodeURIComponent(data.category)}&limit=3`);
        const similairesData = await similairesResp.json();
        setSimilaires((similairesData.realisations || []).filter(r => r.slug !== slug));
      }
    } catch (error) {
      console.error('Erreur chargement réalisation:', error);
      navigate('/realisations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF6600]"></div>
      </div>
    );
  }

  if (!realisation) {
    return null;
  }

  const pageUrl = `https://orion-industrie.eu/realisations/${slug}`;
  const seoTitle = realisation.seo_title || `${realisation.service_type || 'Installation'} ${realisation.category} ${realisation.city} | Orion Industrie`;
  const seoDescription = realisation.seo_description || 
    `${realisation.service_type || 'Installation'} ${realisation.category.toLowerCase()} à ${realisation.city}${realisation.region ? ` (${realisation.region})` : ''}. Expertise Orion Industrie.`.slice(0, 160);

  // Breadcrumb
  const breadcrumbItems = [
    { label: 'Accueil', url: '/' },
    { label: 'Réalisations', url: '/realisations' },
    { label: realisation.city }
  ];

  // ALT image descriptif
  const getImageAlt = (context = 'principale') => {
    return `${realisation.service_type || 'Installation'} ${realisation.category} à ${realisation.city}${context !== 'principale' ? ` - ${context}` : ''} - Orion Industrie`;
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {realisation.seo_keywords && <meta name="keywords" content={realisation.seo_keywords} />}
        <link rel="canonical" href={pageUrl} />
        
        {/* Contrôle indexation admin */}
        {(!realisation.indexable || realisation.status !== 'published') && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={realisation.main_image} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={realisation.main_image} />
      </Helmet>

      {/* Schema.org complets (3 schemas) */}
      <SchemaOrgRealisationSEO realisation={realisation} />

      <Header />

      {/* Breadcrumb SEO crawlable */}
      <BreadcrumbSEO items={breadcrumbItems} />

      <main className="bg-white">
        {/* Hero avec image principale */}
        <section className="relative h-[60vh] md:h-[70vh] overflow-hidden">
          <img
            src={realisation.main_image}
            alt={getImageAlt('principale')}
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="container mx-auto">
              <span className="inline-block bg-[#FF6600] text-white text-sm font-semibold px-4 py-1 rounded-full mb-4">
                {realisation.category}
              </span>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                {realisation.h1 || `${realisation.service_type || 'Installation'} ${realisation.category} à ${realisation.city}`}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-lg">
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>{realisation.city}{realisation.department && ` (${realisation.department})`}</span>
                </div>
                {realisation.date && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>{realisation.date}</span>
                  </div>
                )}
                {realisation.service_type && (
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {realisation.service_type}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Contenu principal */}
        <section className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction géolocalisée */}
            {realisation.intro && (
              <div className="text-xl text-gray-700 mb-12 leading-relaxed">
                {realisation.intro}
              </div>
            )}

            {/* Le problème rencontré */}
            {realisation.problem && (
              <div className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-[#020d2a] mb-4">
                  {realisation.h2_problem || "Le problème rencontré"}
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>{realisation.problem}</p>
                </div>
              </div>
            )}

            {/* La solution Orion Industrie */}
            {realisation.solution && (
              <div className="mb-12 bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl md:text-3xl font-bold text-[#020d2a] mb-4">
                  {realisation.h2_solution || "La solution Orion Industrie"}
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>{realisation.solution}</p>
                </div>
              </div>
            )}

            {/* Équipements installés */}
            {realisation.equipments && realisation.equipments.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold text-[#020d2a] mb-4">Équipements installés</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {realisation.equipments.map((equip, idx) => (
                    <div key={idx} className="flex items-start">
                      <svg className="w-6 h-6 text-[#FF6600] mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-lg text-gray-700">{equip}</span>
                    </div>
                  ))}
                </div>
                {realisation.equipments_details && (
                  <div className="mt-4 text-gray-600">
                    <p>{realisation.equipments_details}</p>
                  </div>
                )}
                {realisation.brand && (
                  <div className="mt-4 text-sm text-gray-500">
                    Marque : <span className="font-medium">{realisation.brand}</span>
                  </div>
                )}
              </div>
            )}

            {/* Les travaux réalisés */}
            {realisation.works && (
              <div className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-[#020d2a] mb-4">
                  {realisation.h2_works || "Les travaux réalisés"}
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>{realisation.works}</p>
                </div>
              </div>
            )}

            {/* Photos avant/après */}
            {(realisation.before_images?.length > 0 || realisation.after_images?.length > 0) && (
              <div className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold text-[#020d2a] mb-6">Avant / Après</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {realisation.before_images?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-700 mb-3">Avant intervention</h4>
                      <div className="grid gap-4">
                        {realisation.before_images.map((img, idx) => (
                          <div key={idx} className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group" onClick={() => setLightboxImage(img)}>
                            <img
                              src={img}
                              alt={getImageAlt('avant travaux')}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {realisation.after_images?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-700 mb-3">Après intervention</h4>
                      <div className="grid gap-4">
                        {realisation.after_images.map((img, idx) => (
                          <div key={idx} className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group" onClick={() => setLightboxImage(img)}>
                            <img
                              src={img}
                              alt={getImageAlt('après travaux')}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Galerie photos supplémentaires */}
            {realisation.gallery_images && realisation.gallery_images.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold text-[#020d2a] mb-6">Galerie</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {realisation.gallery_images.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setLightboxImage(img)}
                    >
                      <img
                        src={img}
                        alt={getImageAlt(`photo ${idx + 1}`)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Le résultat */}
            {realisation.result_text && (
              <div className="mb-12 border-l-4 border-[#FF6600] pl-6">
                <h2 className="text-2xl md:text-3xl font-bold text-[#020d2a] mb-4">
                  {realisation.h2_result || "Le résultat"}
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>{realisation.result_text}</p>
                </div>
              </div>
            )}

            {/* Conclusion */}
            {realisation.conclusion && (
              <div className="mb-12 text-lg text-gray-700">
                <p>{realisation.conclusion}</p>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#020d2a] to-[#0a1a3a] text-white p-8 rounded-xl text-center mb-12">
              <h3 className="text-2xl font-bold mb-3">Un projet similaire à {realisation.city} ?</h3>
              <p className="text-lg text-gray-300 mb-6">
                Demandez un devis gratuit pour votre installation d'équipements de quai
              </p>
              <Link
                to="/#contact"
                className="inline-block bg-[#FF6600] hover:bg-[#e55a00] text-white font-bold py-3 px-8 rounded-lg transition-colors"
              >
                Demander un devis gratuit
              </Link>
            </div>

            {/* Maillage interne : Services et Produits liés */}
            {(realisation.related_services?.length > 0 || realisation.related_products?.length > 0) && (
              <div className="mb-12 bg-blue-50 p-8 rounded-xl border border-blue-100">
                <h3 className="text-xl md:text-2xl font-bold text-[#020d2a] mb-6">Nos prestations et produits</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Services liés */}
                  {realisation.related_services?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <ExternalLink className="w-5 h-5 mr-2 text-[#FF6600]" />
                        Prestations associées
                      </h4>
                      <ul className="space-y-2">
                        {realisation.related_services.map((url, idx) => (
                          <li key={idx}>
                            <Link
                              to={url}
                              className="text-[#020d2a] hover:text-[#FF6600] flex items-center group"
                            >
                              <ArrowRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                              {url.split('/').pop().split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Produits liés */}
                  {realisation.related_products?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <ExternalLink className="w-5 h-5 mr-2 text-[#FF6600]" />
                        Équipements concernés
                      </h4>
                      <ul className="space-y-2">
                        {realisation.related_products.map((url, idx) => (
                          <li key={idx}>
                            <Link
                              to={url}
                              className="text-[#020d2a] hover:text-[#FF6600] flex items-center group"
                            >
                              <ArrowRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                              {url.includes('#') ? url.split('#').pop().split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Nos produits'}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Réalisations similaires */}
        {similaires.length > 0 && (
          <section className="bg-gray-50 py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-[#020d2a] mb-2 text-center">
                Réalisations similaires
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Découvrez d'autres chantiers {realisation.region && `en ${realisation.region}`}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {similaires.map((sim) => (
                  <article
                    key={sim.id}
                    onClick={() => navigate(`/realisations/${sim.slug}`)}
                    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer group"
                  >
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img
                        src={sim.main_image}
                        alt={`${sim.service_type || 'Installation'} ${sim.category} à ${sim.city}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs bg-[#FF6600] text-white px-2 py-1 rounded-full">
                        {sim.category}
                      </span>
                      <h3 className="font-bold text-[#020d2a] mt-2 mb-2 line-clamp-2 group-hover:text-[#FF6600] transition-colors">
                        {sim.title}
                      </h3>
                      <p className="text-sm text-gray-600">{sim.city}{sim.region && ` • ${sim.region}`}</p>
                    </div>
                  </article>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/realisations"
                  className="inline-block text-[#FF6600] hover:text-[#e55a00] font-semibold"
                >
                  Voir toutes nos réalisations →
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FF6600] transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Vue agrandie"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      <Footer />
    </>
  );
};

export default RealisationDetailPage;
